import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, Check } from "lucide-react";

/**
 * Full-month schedule preview + date picker.
 *
 * Opens over the top of the job/convert form so the office can see every
 * other booked job at a glance BEFORE committing a start/end date on the
 * current one. Clicking a day updates the local `start`/`end` state;
 * "Use these dates" fires `onConfirm({ start, end })` back to the caller.
 *
 * Existing jobs render as compact status-coloured pills on each day cell
 * (max 3 shown per day, then `+N more`). The current job (if `excludeJobId`
 * is supplied) is filtered out so the shop sees an accurate picture of
 * *other* bookings.
 */

const STATUS_COLORS = {
  unbooked:               "bg-slate-500/60 border-slate-400/40 text-white",
  booked:                 "bg-blue-500/70 border-blue-400/50 text-white",
  in_store:               "bg-blue-500/70 border-blue-400/50 text-white",
  wash_down:              "bg-cyan-500/70 border-cyan-400/50 text-white",
  disassembly:            "bg-amber-500/80 border-amber-400/60 text-black",
  wrapping:               "bg-amber-500/80 border-amber-400/60 text-black",
  assembly:               "bg-amber-500/80 border-amber-400/60 text-black",
  quality_check:          "bg-amber-500/80 border-amber-400/60 text-black",
  clay_bar:               "bg-amber-500/80 border-amber-400/60 text-black",
  ready_for_collection:   "bg-emerald-500/70 border-emerald-400/60 text-white",
  collected:              "bg-emerald-500/70 border-emerald-400/60 text-white",
  completed:              "bg-emerald-500/70 border-emerald-400/60 text-white",
  cancelled:              "bg-red-500/40 border-red-400/40 text-white line-through opacity-60",
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function startOfMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const dow = first.getDay();
  const shift = dow === 0 ? 6 : dow - 1;
  const start = new Date(first);
  start.setDate(first.getDate() - shift);
  start.setHours(0, 0, 0, 0);
  return start;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

export default function SchedulePreviewModal({
  initialStart = "",
  initialEnd = "",
  excludeJobId = "",
  onClose,
  onConfirm,
}) {
  const now = new Date();
  const [monthOffset, setMonthOffset] = useState(() => {
    const anchor = parseYMD(initialStart) || now;
    return (anchor.getFullYear() - now.getFullYear()) * 12 + (anchor.getMonth() - now.getMonth());
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(initialStart || "");
  const [end, setEnd] = useState(initialEnd || "");

  useEffect(() => {
    api
      .get("/jobs", { params: { main_status: "job,archived" } })
      .then(({ data }) => setJobs(Array.isArray(data) ? data : []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const year = now.getFullYear() + Math.floor((now.getMonth() + monthOffset) / 12);
  const month = ((now.getMonth() + monthOffset) % 12 + 12) % 12;

  // Bucket jobs by day for the visible month grid (6 weeks × 7 days).
  const cells = useMemo(() => {
    const gridStart = startOfMonthGrid(year, month);
    const cellList = [];
    for (let i = 0; i < 42; i++) {
      const d = addDays(gridStart, i);
      const key = ymd(d);
      cellList.push({ date: d, key, inMonth: d.getMonth() === month, events: [] });
    }
    const byKey = Object.fromEntries(cellList.map((c) => [c.key, c]));
    for (const j of jobs) {
      if (excludeJobId && j.id === excludeJobId) continue;
      if (!j.scheduled_date) continue;
      const s = parseYMD(j.scheduled_date);
      const e = parseYMD(j.scheduled_end_date) || s;
      if (!s) continue;
      // Expand every day the job spans
      for (let d = new Date(s); d <= e; d = addDays(d, 1)) {
        const k = ymd(d);
        if (byKey[k]) byKey[k].events.push(j);
      }
    }
    return cellList;
  }, [jobs, year, month, excludeJobId]);

  const handleDayClick = (key) => {
    // First click sets START. Second click sets END (if after start) OR
    // resets to that day if before/equal. Third+ click resets start.
    if (!start || (start && end)) {
      setStart(key);
      setEnd("");
      return;
    }
    if (key < start) {
      setStart(key);
      return;
    }
    setEnd(key === start ? "" : key);
  };

  const inRange = (key) => {
    if (!start) return false;
    if (start && !end) return key === start;
    return key >= start && key <= end;
  };

  const confirm = () => {
    onConfirm({ start, end });
  };

  // Split the 42 cells into 6 week-rows so we can overlay a continuous
  // "selected range" bar across each week — same visual language as the
  // /calendar tab's event bars.
  const weeks = useMemo(() => {
    const out = [];
    for (let w = 0; w < 6; w++) out.push(cells.slice(w * 7, w * 7 + 7));
    return out;
  }, [cells]);

  /** For a week-row of 7 cells, return `null` if the current selection
   * doesn't intersect it, otherwise `{startCol, endCol}` 0-6 indexes
   * clipped to the week bounds. */
  const weekSelection = (week) => {
    if (!start) return null;
    const weekFirst = week[0].key;
    const weekLast  = week[6].key;
    const selStart = start;
    const selEnd   = end || start;
    // No overlap
    if (selEnd < weekFirst || selStart > weekLast) return null;
    let startCol = 0;
    for (let i = 0; i < 7; i++) if (week[i].key >= selStart) { startCol = i; break; }
    let endCol = 6;
    for (let i = 6; i >= 0; i--) if (week[i].key <= selEnd) { endCol = i; break; }
    const isStartHere = selStart >= weekFirst && selStart <= weekLast;
    const isEndHere   = selEnd   >= weekFirst && selEnd   <= weekLast;
    return { startCol, endCol, isStartHere, isEndHere };
  };

  return createPortal(
    (<div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-6 md:pl-[20rem] bg-black/60 backdrop-blur-sm"
      data-testid="schedule-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="glass-strong rounded-3xl w-full max-w-5xl max-h-[94vh] flex flex-col border border-white/10 shadow-2xl">
        {/* ---- Header ---- */}
        <div className="flex items-start sm:items-center justify-between gap-3 px-5 md:px-8 py-4 md:py-5 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              <CalendarIcon className="w-4 h-4 md:w-5 md:h-5 text-slate-300" />
            </div>
            <div className="min-w-0">
              <div className="text-white text-sm md:text-base font-semibold">Pick a date on the shop schedule</div>
              <div className="hidden md:block text-xs lg:text-sm text-slate-400 mt-0.5 truncate">
                Click a day to set the start, click another day for the end. Every other booking is shown so you can dodge clashes.
              </div>
              <div className="md:hidden text-[11px] text-slate-400 mt-0.5 leading-snug">
                Click a day to set the start, click another for the end.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="schedule-preview-close"
            className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>

        {/* ---- Month nav ---- */}
        <div className="flex items-center justify-between px-5 md:px-8 pt-4">
          <button
            type="button"
            onClick={() => setMonthOffset((v) => v - 1)}
            data-testid="schedule-preview-prev"
            className="p-2 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <div className="text-white text-lg md:text-xl font-medium">
            {MONTHS[month]} <span className="text-slate-400 font-light">{year}</span>
          </div>
          <button
            type="button"
            onClick={() => setMonthOffset((v) => v + 1)}
            data-testid="schedule-preview-next"
            className="p-2 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>

        {/* ---- Grid ---- */}
        <div className="flex-1 overflow-y-auto px-5 md:px-8 pb-4">
          <div className="grid grid-cols-7 gap-1 md:gap-2 mt-3 md:mt-4 text-[10px] md:text-xs uppercase tracking-widest text-slate-500">
            {DOW.map((d) => (
              <div key={d} className="text-center py-1">{d}</div>
            ))}
          </div>

          {loading ? (
            <div className="text-center text-slate-400 text-sm py-16" data-testid="schedule-preview-loading">
              Loading shop schedule…
            </div>
          ) : (
            <div className="flex flex-col gap-1 md:gap-2 mt-1">
              {weeks.map((week, wi) => {
                const sel = weekSelection(week);
                return (
                  <div key={wi} className="relative">
                    {/* Continuous selection bar — spans the picked range
                        across this week, like the /calendar event bars. */}
                    {sel && (
                      <div
                        className="pointer-events-none absolute top-1 bottom-1 z-0"
                        style={{
                          left:  `calc((100% / 7) * ${sel.startCol} + 2px)`,
                          width: `calc((100% / 7) * ${sel.endCol - sel.startCol + 1} - 4px)`,
                        }}
                        data-testid={`schedule-preview-range-${wi}`}
                      >
                        <div
                          className={[
                            "h-full border border-emerald-400/70 bg-emerald-500/15",
                            sel.isStartHere ? "rounded-l-xl md:rounded-l-2xl" : "border-l-0",
                            sel.isEndHere   ? "rounded-r-xl md:rounded-r-2xl" : "border-r-0",
                            "shadow-[0_0_0_1px_rgba(52,211,153,0.25)_inset]",
                          ].join(" ")}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-7 gap-1 md:gap-2 relative z-10">
                      {week.map((c) => {
                        const isToday = c.key === ymd(now);
                        const isStart = c.key === start;
                        const isEnd = c.key === end;
                        const MAX_VISIBLE_DESKTOP = 5;
                        const MAX_VISIBLE_MOBILE = 3;
                        return (
                          <button
                            type="button"
                            key={c.key}
                            onClick={() => handleDayClick(c.key)}
                            data-testid={`schedule-day-${c.key}`}
                            className={[
                              "text-left rounded-xl md:rounded-2xl border p-1.5 md:p-2.5 min-h-[88px] md:min-h-[128px] lg:min-h-[140px] flex flex-col gap-1 md:gap-1.5 transition group bg-transparent",
                              c.inMonth
                                ? "border-white/10 hover:border-white/25 hover:bg-white/[0.03]"
                                : "border-white/5 opacity-60 hover:opacity-90",
                            ].join(" ")}
                          >
                            <div className="flex items-center justify-between">
                              <span
                                className={[
                                  "text-xs md:text-sm font-medium",
                                  isToday ? "text-white bg-blue-500/70 rounded-full w-5 h-5 md:w-6 md:h-6 flex items-center justify-center" : "text-slate-300",
                                  isStart || isEnd ? "!text-emerald-200 font-semibold" : "",
                                ].join(" ")}
                              >
                                {c.date.getDate()}
                              </span>
                              {(isStart || isEnd) && (
                                <span className="text-[9px] md:text-[10px] uppercase tracking-widest text-emerald-200 font-semibold">
                                  {isStart && isEnd ? "Day" : isStart ? "Start" : "End"}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col gap-0.5 md:gap-1 overflow-hidden">
                              {/* Mobile: show 3 events */}
                              <div className="md:hidden flex flex-col gap-0.5">
                                {c.events.slice(0, MAX_VISIBLE_MOBILE).map((ev, i) => (
                                  <div
                                    key={ev.id + "-m-" + i}
                                    className={[
                                      "text-[10px] leading-tight rounded-md px-1.5 py-0.5 border truncate",
                                      STATUS_COLORS[ev.sub_status] || STATUS_COLORS.booked,
                                    ].join(" ")}
                                    title={`${ev.vehicle || "Job"} — ${ev.contact_name || ""}`}
                                  >
                                    {ev.vehicle || ev.contact_name || "Job"}
                                  </div>
                                ))}
                                {c.events.length > MAX_VISIBLE_MOBILE && (
                                  <div className="text-[10px] text-slate-400">+{c.events.length - MAX_VISIBLE_MOBILE} more</div>
                                )}
                              </div>
                              {/* Desktop: show 5 with vehicle + contact name on two lines */}
                              <div className="hidden md:flex md:flex-col md:gap-1">
                                {c.events.slice(0, MAX_VISIBLE_DESKTOP).map((ev, i) => (
                                  <div
                                    key={ev.id + "-d-" + i}
                                    className={[
                                      "text-[11px] leading-tight rounded-md px-2 py-1 border",
                                      STATUS_COLORS[ev.sub_status] || STATUS_COLORS.booked,
                                    ].join(" ")}
                                    title={`${ev.vehicle || "Job"} — ${ev.contact_name || ""}`}
                                  >
                                    <div className="truncate font-medium">{ev.vehicle || "Job"}</div>
                                    {ev.contact_name && (
                                      <div className="truncate opacity-70">{ev.contact_name}</div>
                                    )}
                                  </div>
                                ))}
                                {c.events.length > MAX_VISIBLE_DESKTOP && (
                                  <div className="text-[11px] text-slate-400 pl-1">+{c.events.length - MAX_VISIBLE_DESKTOP} more</div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ---- Footer with pick summary + action ---- */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 px-5 md:px-8 py-4 md:py-5 border-t border-white/10">
          <div className="flex items-center gap-4 md:gap-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Start</div>
              <div className="text-white font-medium whitespace-nowrap" data-testid="schedule-preview-start">
                {start || <span className="text-slate-500 font-normal">pick a day</span>}
              </div>
            </div>
            <div className="text-slate-600">→</div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">End</div>
              <div className="text-white font-medium whitespace-nowrap" data-testid="schedule-preview-end">
                {end || <span className="text-slate-500 font-normal">(optional)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <button
              type="button"
              onClick={() => { setStart(""); setEnd(""); }}
              data-testid="schedule-preview-clear"
              className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white hover:bg-white/10 transition whitespace-nowrap"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm text-slate-300 hover:text-white hover:bg-white/10 transition whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={!start}
              data-testid="schedule-preview-confirm"
              className="px-5 py-2 rounded-full text-sm font-semibold bg-emerald-500 text-black hover:bg-emerald-400 transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <Check className="w-4 h-4" /> Use these dates
            </button>
          </div>
        </div>
      </div>
    </div>),
    document.body
  );
}
